import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { Badge } from "../../../ui/components/Badge";
import { IconButton } from "../../../ui/components/IconButton";
import Icon from "../../../base/components/Icon";
import buttonDropdownStyles from "../../../ui/components/ButtonDropdown/ButtonDropdown.module.scss";
import styles from "./storeTableUi.module.scss";

export const PlusIcon = () => <Icon icon="plus" />;
export const PencilIcon = () => <Icon icon="pencil" />;
export const DeleteIcon = () => <Icon icon="delete" />;
export const GeneratePasswordIcon = () => <Icon icon="generate-password" />;

export const DotsIcon = () => <Icon icon="more" />;

const iconForActionItem = item => {
    if (item.icon) return item.icon;
    const id = String(item.id || "").toLowerCase();
    const label = String(item.label || "").toLowerCase();
    if (
        id === "update" ||
        id === "edit" ||
        label === "edit" ||
        label.startsWith("edit ")
    ) {
        return <PencilIcon />;
    }
    if (id === "delete" || label === "delete" || label.startsWith("delete ")) {
        return <DeleteIcon />;
    }
    if (
        id === "generate-password" ||
        label === "generate password" ||
        label.startsWith("generate password")
    ) {
        return <GeneratePasswordIcon />;
    }
    return null;
};

const isDestructiveActionItem = item => {
    if (item.destructive) return true;
    const id = String(item.id || "").toLowerCase();
    const label = String(item.label || "").toLowerCase();
    return (
        id === "delete" ||
        label === "delete" ||
        label.startsWith("delete ") ||
        id === "generate-password" ||
        label === "generate password" ||
        label.startsWith("generate password")
    );
};

/** Map legacy status__* colors to DS Badge variants (visual only). */
export const legacyColorToBadge = color => {
    if (color === "success") return "primary";
    if (color === "info") return "secondary";
    if (color === "error") return "neutral";
    return "neutral";
};

/** Normalize array / comma string / single id into a clean list. */
export const normalizeProviderIds = value => {
    if (value == null || value === "") return [];
    const list = Array.isArray(value)
        ? value
        : String(value)
              .split(",")
              .map(part => part.trim());
    return list.map(String).filter(Boolean);
};

export function StoresProviderBadges({ value }) {
    const ids = normalizeProviderIds(value);
    if (ids.length === 0) return "—";

    return (
        <span className={styles.providerBadges}>
            {ids.map(id => (
                <Badge key={id} variant="primary">
                    {id}
                </Badge>
            ))}
        </span>
    );
}

/**
 * Row ⋮ menu used across migrated store tables.
 * Pass `items` for custom actions; otherwise uses Edit + Delete.
 * Edit / Delete get pencil / trash icons by default (also by id/label).
 * @param {{
 *   disabled?: boolean,
 *   label: string,
 *   onUpdate?: () => void,
 *   onDelete?: () => void,
 *   updateLabel?: string,
 *   deleteLabel?: string,
 *   items?: Array<{ id: string, label: string, onSelect: () => void, disabled?: boolean, icon?: import('react').ReactNode, destructive?: boolean }>,
 * }} props
 */
export function RowActionsMenu({
    disabled,
    label,
    onUpdate,
    onDelete,
    updateLabel = "Edit",
    deleteLabel = "Delete",
    items,
}) {
    const [open, setOpen] = useState(false);

    const menuItems =
        items ??
        [
            onUpdate
                ? {
                      id: "update",
                      label: updateLabel,
                      icon: <PencilIcon />,
                      onSelect: onUpdate,
                  }
                : null,
            onDelete
                ? {
                      id: "delete",
                      label: deleteLabel,
                      icon: <DeleteIcon />,
                      destructive: true,
                      onSelect: onDelete,
                  }
                : null,
        ].filter(Boolean);

    return (
        <DropdownMenu.Root onOpenChange={setOpen} open={open}>
            <DropdownMenu.Trigger asChild>
                <IconButton
                    aria-label={`Actions for ${label}`}
                    disabled={disabled}
                    onClick={event => event.stopPropagation()}
                    size="sm"
                    type="button"
                    variant="tertiary"
                >
                    <DotsIcon />
                </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="start"
                    className={`${buttonDropdownStyles.content} ${buttonDropdownStyles.contentActions}`}
                    loop
                    side="bottom"
                    sideOffset={6}
                >
                    <DropdownMenu.Label
                        className={buttonDropdownStyles.menuTitle}
                    >
                        Actions
                    </DropdownMenu.Label>
                    {menuItems.map(item => {
                        const icon = iconForActionItem(item);
                        const destructive = isDestructiveActionItem(item);
                        return (
                            <DropdownMenu.Item
                                className={`${buttonDropdownStyles.item}${
                                    destructive
                                        ? ` ${buttonDropdownStyles.itemDestructive}`
                                        : ""
                                }`}
                                disabled={disabled || item.disabled}
                                key={item.id}
                                onSelect={item.onSelect}
                            >
                                {icon ? (
                                    <span
                                        aria-hidden
                                        className={
                                            buttonDropdownStyles.itemIcon
                                        }
                                    >
                                        {icon}
                                    </span>
                                ) : null}
                                {item.label}
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

export { styles as storeTableUiStyles };
